/**
 * RecodeForm → canonical RECODE text (exactly what gets written to the editor).
 *
 *   bucket      → `RECODE <t>::<<opt>> = <premise>` (single) or block (≥2)
 *   select_n    → `RECODE <t> = SELECT_N(<<src>>, N, {<<p1>>, ...})`
 *   arithmetic  → `RECODE <t> = <raw expression>`
 *
 * Targets are emitted bare when safe, otherwise `<<...>>`; options/sources are
 * always `<<...>>` (matches the backend fixtures and the IF generator).
 */
import { quoteLabel, isBareLabelSafe } from "../logicWizard/grammar"
import { generateExpression } from "../logicWizard/ifGenerator"
import { RecodeForm } from "./recodeModel"

function serializeTarget(name: string): string {
  return isBareLabelSafe(name) ? name : quoteLabel(name)
}

export function generateRecode(form: RecodeForm): string {
  switch (form.mode) {
    case "bucket": {
      const target = serializeTarget(form.target)
      const lines = form.buckets.map(
        (b) =>
          `${target}::${quoteLabel(b.option)} = ${generateExpression(b.ast)}`,
      )
      if (lines.length === 0) return "RECODE"
      if (lines.length === 1) return `RECODE ${lines[0]}`
      return `RECODE\n${lines.join("\n")}`
    }
    case "select_n": {
      const { source, n, priority } = form.selectN
      const prio = priority.map(quoteLabel).join(", ")
      return `RECODE ${serializeTarget(form.target)} = SELECT_N(${quoteLabel(
        source,
      )}, ${n}, {${prio}})`
    }
    case "arithmetic":
      return `RECODE ${serializeTarget(form.target)} = ${form.arithmeticText.trim()}`
  }
}
